// Restore Wellness official emblem — recreated as inline SVG in dimensional gold.
// Sharp central spike + 6 symmetric pairs of tapered fern blades, inside a thin ring.
// Uses a multi-stop linear gradient (light → antique gold → deep bronze) plus a
// highlight overlay + soft drop-shadow to give it real depth.

let uid = 0;
const nextId = () => `rm${(++uid).toString(36)}`;

export default function RestoreMark({ size = 48, tone = "gold", className = "" }) {
  const gid = nextId();
  const gGold = `${gid}-gold`;
  const gHilite = `${gid}-hilite`;
  const gShadow = `${gid}-shadow`;

  const goldStops = tone === "gold"
    ? [
        { offset: "0%", color: "#fbe6a2" },
        { offset: "22%", color: "#e8c46a" },
        { offset: "50%", color: "#c69439" },
        { offset: "78%", color: "#8a5f1c" },
        { offset: "100%", color: "#4d3308" },
      ]
    : [
        { offset: "0%", color: "#ffffff" },
        { offset: "100%", color: "#d3ccbb" },
      ];

  // Leaf pairs: [y position on stem, length, angle in degrees above horizontal]
  // Ordered top → bottom. Bottom leaves are longest and most horizontal.
  const leaves = [
    { y: 30, len: 14, ang: 46 },
    { y: 38, len: 20, ang: 40 },
    { y: 47, len: 26, ang: 34 },
    { y: 56, len: 30, ang: 28 },
    { y: 65, len: 32, ang: 23 },
    { y: 73, len: 33, ang: 18 },
  ];

  const rad = (d) => (d * Math.PI) / 180;

  // Build a single tapered blade path anchored at the stem (x0, y) extending
  // outward at `ang` above horizontal for `len` units. `dir` = +1 for right, -1 for left.
  const blade = ({ y, len, ang, dir }) => {
    const x0 = 50 + dir * 1.4;              // small offset off the stem
    const tipX = 50 + dir * len;
    const tipY = y - Math.sin(rad(ang)) * len * 0.85;
    // widen the root ~2.6 units (perpendicular to blade direction)
    const rootTopY = y - 1.4;
    const rootBotY = y + 1.4;
    return `M ${x0} ${rootTopY} Q ${50 + dir * len * 0.55} ${y - Math.sin(rad(ang)) * len * 0.5}, ${tipX} ${tipY} Q ${50 + dir * len * 0.5} ${y - Math.sin(rad(ang)) * len * 0.35}, ${x0} ${rootBotY} Z`;
  };

  // Central spike: long thin diamond narrowing to a sharp point at the top and a
  // gentler point at the bottom.
  const spike =
    "M 50 12 " +
    "L 51.6 55 " +
    "L 51.1 84 " +
    "L 50 88 " +
    "L 48.9 84 " +
    "L 48.4 55 " +
    "Z";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Restore Wellness"
    >
      <defs>
        {/* Main gold gradient */}
        <linearGradient id={gGold} x1="0.15" y1="0.05" x2="0.9" y2="0.95">
          {goldStops.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        {/* Diagonal highlight sheen — subtle, adds dimension without washing out */}
        <linearGradient id={gHilite} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="45%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
        </linearGradient>
        {/* Subtle drop shadow */}
        <filter id={gShadow} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0.4" dy="0.8" stdDeviation="0.9" floodColor="#4b3510" floodOpacity="0.35" />
        </filter>
      </defs>

      <g filter={`url(#${gShadow})`}>
        {/* Outer ring */}
        <circle
          cx="50"
          cy="50"
          r="44"
          fill="none"
          stroke={`url(#${gGold})`}
          strokeWidth="3"
        />

        {/* Central spike */}
        <path d={spike} fill={`url(#${gGold})`} />

        {/* Symmetric leaf pairs */}
        {leaves.map((leaf, i) => (
          <g key={i}>
            <path d={blade({ ...leaf, dir: -1 })} fill={`url(#${gGold})`} />
            <path d={blade({ ...leaf, dir: 1 })} fill={`url(#${gGold})`} />
          </g>
        ))}

        {/* Highlight overlay clipped to the emblem elements for dimension */}
        <g style={{ mixBlendMode: "soft-light" }} opacity="0.7">
          <circle cx="50" cy="50" r="44" fill="none" stroke={`url(#${gHilite})`} strokeWidth="3" />
          <path d={spike} fill={`url(#${gHilite})`} />
          {leaves.map((leaf, i) => (
            <g key={`h${i}`}>
              <path d={blade({ ...leaf, dir: -1 })} fill={`url(#${gHilite})`} />
              <path d={blade({ ...leaf, dir: 1 })} fill={`url(#${gHilite})`} />
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}
